import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useTranslation from '../../customHooks/useTranslation';
import useLanguage from '../../customHooks/useLanguage';
import LogoMark from './LogoMark';

export const SHEETS = [
	{ id: 'cooperation', code: 'A-101' },
	{ id: 'scope', code: 'A-201' },
	{ id: 'stack', code: 'A-301' },
	{ id: 'about', code: 'A-401' },
	{ id: 'contact', code: 'A-501' },
] as const;

export interface SheetLink {
	id: string;
	code: string;
	label: string;
}

interface HeaderProps {
	// The sheet index of the current drawing set; defaults to the main page.
	sheets?: SheetLink[];
	// Path of this page without the language prefix: '' for the main page, '/mentoring'.
	page?: string;
	cta?: { href: string; label: string };
}

export default function Header({ sheets: sheetsProp, page = '', cta }: HeaderProps) {
	const t = useTranslation();
	const { language } = useLanguage();
	const sheets = sheetsProp ?? SHEETS.map(s => ({ ...s, label: t.nav.sheets[s.id] }));
	const action = cta ?? { href: '#book', label: t.nav.book };
	const [open, setOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open]);

	const otherLanguagePath = language === 'pl' ? `/en${page}` : page || '/pl';
	const homePath = language === 'pl' ? '/' : '/en';
	const otherLanguage = language === 'pl' ? 'en' : 'pl';

	return (
		<header className={`sticky top-0 z-40 border-b transition-colors duration-300 ${scrolled || open ? 'border-ink-line bg-sheet/95 backdrop-blur-xs' : 'border-transparent bg-sheet'}`}>
			<div className='mx-auto flex h-16 max-w-[1320px] items-center gap-6 px-4 sm:px-8 pt-2'>
				{page ? (
					<Link to={homePath} className='group flex min-w-0 items-center gap-3' aria-label={t.nav.home}>
						<Brand role={t.hero.role} />
					</Link>
				) : (
					<a href='#top' className='group flex min-w-0 items-center gap-3' aria-label={t.nav.home}>
						<Brand role={t.hero.role} />
					</a>
				)}

				<nav aria-label={t.nav.index} className='ml-auto hidden lg:block'>
					<ul className='flex items-center'>
						{sheets.map(sheet => (
							<li key={sheet.id}>
								<a href={`#${sheet.id}`} className='group flex flex-col items-start gap-0.5 whitespace-nowrap px-2.5 py-2 text-[0.92rem] leading-none text-ink-soft xl:flex-row xl:items-baseline xl:gap-2 xl:px-3 xl:leading-normal transition-colors hover:text-ink'>
									<span className='lettering text-[0.66rem] text-ink-faint transition-colors group-hover:text-redline'>{sheet.code}</span>
									{sheet.label}
								</a>
							</li>
						))}
					</ul>
				</nav>

				<div className='ml-auto flex items-center gap-3 lg:ml-0'>
					<Link to={otherLanguagePath} hrefLang={otherLanguage} lang={otherLanguage} aria-label={t.nav.language} className='lettering border border-ink-line px-2.5 py-2 text-label text-ink-soft transition-colors hover:border-ink-soft hover:text-ink'>
						{t.nav.languageShort}
					</Link>
					<a href={action.href} className='btn-redline hidden shrink-0 whitespace-nowrap px-4 py-2.5 sm:inline-flex'>
						{action.label}
					</a>
					<button type='button' className='flex h-10 w-10 items-center justify-center border border-ink-line text-ink lg:hidden' aria-expanded={open} aria-controls='sheet-index' aria-label={open ? t.nav.close : t.nav.menu} onClick={() => setOpen(v => !v)}>
						<svg viewBox='0 0 20 20' className='h-5 w-5' aria-hidden='true'>
							{open ? <path d='M4 4l12 12M16 4 4 16' stroke='currentColor' strokeWidth='1.5' /> : <path d='M3 6h14M3 10h14M3 14h14' stroke='currentColor' strokeWidth='1.5' />}
						</svg>
					</button>
				</div>
			</div>

			<nav id='sheet-index' aria-label={t.nav.index} hidden={!open} className='border-t border-ink-line bg-sheet lg:hidden'>
				<ul className='mx-auto max-w-[1320px] px-4 py-2 sm:px-8'>
					{sheets.map(sheet => (
						<li key={sheet.id} className='border-b border-ink-line/60 last:border-0'>
							<a href={`#${sheet.id}`} onClick={() => setOpen(false)} className='flex items-baseline justify-between py-4 text-lg text-ink'>
								{sheet.label}
								<span className='lettering text-label text-ink-faint'>{sheet.code}</span>
							</a>
						</li>
					))}
					<li className='py-4 sm:hidden'>
						<a href={action.href} onClick={() => setOpen(false)} className='btn-redline w-full'>
							{action.label}
						</a>
					</li>
				</ul>
			</nav>
		</header>
	);
}

function Brand({ role }: { role: string }) {
	return (
		<>
			<LogoMark className='h-8 w-8 shrink-0' />
			<span className='flex min-w-0 flex-col leading-none'>
				<span className='type-wide truncate text-[0.95rem] font-bold tracking-[-0.01em] text-ink'>Kamil Pfaff</span>
				<span className='lettering mt-1 truncate text-[0.68rem] text-ink-faint'>{role}</span>
			</span>
		</>
	);
}
